import React, { useCallback, useMemo } from "react";
import { AHSwitch, Box, ListItem, SectionHeader } from "arkh";
import { TaskTypesType } from "../../../types/TaskUIType";
import {
  Connection,
  Section,
  Task,
  TaskCheck,
  TaskNumber,
} from "../State/Routine/types";
import { FormattedMessage } from "react-intl";
import { TaskResponseType } from "./TaskResponseType/TaskResponseType";
import { TaskConnections } from "./TaskConnections";
import { ScrollView } from "react-native";
import { useListContentStyle } from "../../../components/mobile/useListContentStyle";

import { RoutineEditOrgTopicListItem } from "../RoutineEditMain/RoutineEditMainOrgTopicListItem";
import { graphql, useFragment } from "react-relay";
import { TaskScreenComponent_topics$key } from "./__generated__/TaskScreenComponent_topics.graphql";

type TaskScreenComponentProps = {
  isOrg: boolean;
  topics: TaskScreenComponent_topics$key | null;
  task: Task;
  hasRemote: boolean;
  sections: Section[];
  connections: Connection[];
  setConnections(_: Connection[]): void;
  taskUpdateGoal(taskId: string, goal: TaskNumber["goal"]): void;
  taskUpdateUnit(taskId: string, unit: TaskNumber["unit"]): void;
  taskUpdateSentiments(
    taskId: string,
    sentiments: TaskCheck["sentiments"]
  ): void;
  setType?(type: TaskTypesType): void;
};

export function TaskScreenComponent(props: TaskScreenComponentProps) {
  const contentContainerStyle = useListContentStyle();
  const topics = useFragment(
    graphql`
      fragment TaskScreenComponent_topics on OrgTopic @relay(plural: true) {
        id
        name
        isPrivate
        ...RoutineEditMainOrgTopicListItem_topics
        ...TaskConnections_topics
      }
    `,
    props.topics
  );
  const showSections = useMemo(
    () => props.sections.length > 1,
    [props.sections.length]
  );
  const currentSingleSection = useMemo(() => {
    if (props.sections.length === 1) {
      return props.sections[0];
    }
    return null;
  }, [props.sections]);
  const currentSingleConnection = useMemo(() => {
    if (currentSingleSection) {
      const taskConnections = props.connections.filter(
        ({ taskId }) => taskId === props.task.id
      );
      if (taskConnections.length === 1) {
        return taskConnections[0];
      }
    }
    return null;
  }, [props.connections, currentSingleSection, props.task.id]);

  const goalValue = useMemo(() => {
    return currentSingleConnection?.value ?? null;
  }, [currentSingleConnection]);

  const currentSingleTopicId = useMemo(() => {
    return currentSingleConnection?.orgTopic?.id ?? null;
  }, [currentSingleConnection]);

  const currentSingleCanBeDisabled = useMemo(() => {
    return currentSingleConnection?.canBeDisabled ?? false;
  }, [currentSingleConnection]);

  const setGoalValue = useCallback(
    (goalValue: number) =>
      props.setConnections(
        props.connections.map((_connection) =>
          _connection.taskId === props.task.id
            ? { ..._connection, value: goalValue }
            : _connection
        )
      ),
    [props]
  );

  return (
    <ScrollView
      automaticallyAdjustKeyboardInsets
      keyboardShouldPersistTaps="handled"
      style={{
        flex: 1,
      }}
      contentContainerStyle={contentContainerStyle}
    >
      <TaskResponseType
        task={props.task}
        setType={props.setType ? props.setType : () => {}}
        canChangeType={!props.hasRemote}
        taskUpdateGoal={props.taskUpdateGoal}
        taskUpdateUnit={props.taskUpdateUnit}
        taskUpdateSentiments={props.taskUpdateSentiments}
        setGoalValue={setGoalValue}
        goalValue={goalValue ?? undefined}
      />
      {props.isOrg && !showSections ? (
        <Box spaceTop="large">
          <Box spaceBottom="large">
            <ListItem
              isFree
              disabled
              border="border-primary"
              right={
                <AHSwitch
                  value={
                    currentSingleSection?.orgCanBeDisabled
                      ? true
                      : (currentSingleCanBeDisabled ?? false)
                  }
                  disabled={!!currentSingleSection?.orgCanBeDisabled}
                  onValueChange={(value) => {
                    props.setConnections(
                      props.connections.map((connection) =>
                        connection.taskId === props.task.id
                          ? { ...connection, canBeDisabled: value }
                          : connection
                      )
                    );
                  }}
                />
              }
            >
              <ListItem.Title>
                <FormattedMessage
                  defaultMessage="Allow units to hide locally"
                  description="WorkspaceRoutineEditSectionSettings allow units to disable"
                />
              </ListItem.Title>
            </ListItem>
          </Box>
          {topics ? (
            <RoutineEditOrgTopicListItem
              topics={topics}
              selectedTopic={currentSingleTopicId ?? "NONE"}
              onSelectedTopicChange={(topicId) => {
                const topic = topics.find((t) => t.id === topicId);
                props.setConnections(
                  props.connections.map((connection) =>
                    currentSingleConnection &&
                    currentSingleConnection.sectionId ===
                      connection.sectionId &&
                    currentSingleConnection.taskId === connection.taskId
                      ? {
                          ...connection,
                          orgTopic: topic
                            ? {
                                id: topic.id,
                                name: topic.name,
                                isPrivate: topic.isPrivate,
                              }
                            : null,
                        }
                      : connection
                  )
                );
              }}
            >
              <ListItem.SubTitle>
                <FormattedMessage
                  defaultMessage="This task will only be visible for units who are assigned to this topic. If the topic is subscribed by one or multiple teams locally, then issues created on this task will automatically be shared with assigned teams."
                  description="TaskScreenComponent task topic sub title"
                />
              </ListItem.SubTitle>
            </RoutineEditOrgTopicListItem>
          ) : null}
        </Box>
      ) : null}

      <Box spaceTop="large" />
      {showSections ? (
        <>
          <SectionHeader>
            <SectionHeader.Title>
              <FormattedMessage
                defaultMessage="Sections"
                description="Task Screen Sections title"
              />
            </SectionHeader.Title>
          </SectionHeader>
          <Box spaceBottom="medium">
            <TaskConnections
              isOrg={props.isOrg}
              task={props.task}
              sections={props.sections}
              connections={props.connections}
              setConnections={props.setConnections}
              topics={topics ?? null}
            />
          </Box>
        </>
      ) : null}
    </ScrollView>
  );
}
